import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { Card, CardTitle } from '@shared/ui/Card';
import { Input } from '@shared/ui/Input';
import { useSetKadryBotTokenMutation } from '@shared/api';
import type { KadryBotChannel, KadryBotStatus } from '@entities/kadry';
import { KadryCardHead, KadryErrorBanner, KadryMuted } from '../ui';
import { CardBody, Line, Row, Steps } from './connectionsShared';

export const BOT_HELP: Record<KadryBotChannel, string[]> = {
  telegram: [
    'Откройте в Telegram @BotFather и отправьте /newbot.',
    'Задайте имя и адрес бота (например, satkom_hr_bot).',
    'Скопируйте токен вида 123456:ABC… и вставьте ниже.',
  ],
  max: [
    'Создайте чат-бота организации на платформе MAX для партнёров (инструкция — dev.max.ru).',
    'Дождитесь, пока бот пройдёт модерацию.',
    'Скопируйте токен бота и вставьте ниже.',
  ],
};

export function BotConnectionsCard({ status }: { status: KadryBotStatus }) {
  const [setToken, state] = useSetKadryBotTokenMutation();
  const [token, setTokenValue] = useState('');
  const title = status.channel === 'telegram' ? 'Бот Telegram' : 'Бот MAX';

  return (
    <Card>
      <KadryCardHead>
        <CardTitle>{title}</CardTitle>
        {status.bot ? (
          <Badge $tone="success">@{status.bot.username}</Badge>
        ) : status.problem === 'unreachable' ? (
          <Badge $tone="warning">нет связи</Badge>
        ) : status.token_set ? (
          <Badge $tone="danger">токен не принят</Badge>
        ) : (
          <Badge $tone="neutral">не подключён</Badge>
        )}
      </KadryCardHead>
      <CardBody>
        {!status.bot && status.problem === 'unreachable' && (
          <KadryMuted>
            Токен сохранён, но сервер ОСНОВЫ не достучался до{' '}
            {status.channel === 'telegram' ? 'Telegram' : 'MAX'}. Чаще всего это блокировка у хостинга —
            проверка повторяется сама раз в минуту. Что сделать — в инструкции запуска Кадров, раздел
            «Ключи и подключения».
          </KadryMuted>
        )}
        {status.link && (
          <Line>
            Ссылка на бота:{' '}
            <a href={status.link} target="_blank" rel="noreferrer">
              {status.link}
            </a>
          </Line>
        )}
        {!status.enabled && (
          <KadryMuted>
            Канал выключен в разделе <Link to="/agents/kadry/settings/gates">«Функции»</Link> — сообщения не
            отправляются.
          </KadryMuted>
        )}
        <Steps>
          {BOT_HELP[status.channel].map((s) => (
            <li key={s}>{s}</li>
          ))}
        </Steps>
        <Row
          onSubmit={async (e) => {
            e.preventDefault();
            try {
              await setToken({ channel: status.channel, token: token.trim() }).unwrap();
              setTokenValue('');
            } catch {
              /* ошибка ниже */
            }
          }}
        >
          <Input
            type="password"
            value={token}
            onChange={(e) => setTokenValue(e.target.value)}
            placeholder={status.token_set ? 'Токен задан — ввести новый' : 'Токен бота'}
            aria-label={`Токен: ${title}`}
            autoComplete="new-password"
          />
          <Button type="submit" $size="sm" disabled={!token.trim() || state.isLoading}>
            {state.isLoading ? 'Проверяем…' : 'Сохранить и проверить'}
          </Button>
        </Row>
        {state.error ? <KadryErrorBanner error={state.error} onDismiss={() => state.reset()} /> : null}
      </CardBody>
    </Card>
  );
}

export { BotConnectionsCard as BotCard };
